/**
 * What a billing limit does to a PROVISIONED machine — the half three surfaces got wrong.
 *
 * ⚠⚠ THE PROMISE "YOUR NODES STAY ONLINE" IS TRUE FOR BANDWIDTH AND FALSE FOR COMPUTE, and that
 * is why this is a shared module rather than three edited sentences (app#163). A bandwidth
 * throttle or block acts on the billable data path and never touches the bridge, so every node —
 * provisioned machines included — really does stay connected. The COMPUTE stop is a different
 * mechanism: a provisioned node's agent runs INSIDE the ECS task, so ending the task takes that
 * node OFFLINE.
 *
 * ⚠ The router defines four stop reasons (`src/tools/compute_stop.go`). Three are billing:
 *
 *   | reason          | who it hits                                                  |
 *   |-----------------|--------------------------------------------------------------|
 *   | StopAllowance   | a Free org reaching its included COMPUTE — a separate axis    |
 *   | StopCreditEmpty | a PAID org whose credit balance is empty (router#1242)        |
 *   | StopLapsed      | any org whose subscription lapsed (`unpaid` / `canceled`)     |
 *
 * The fourth, `StopDeadline`, is the customer's own `max_hours` bound and is deliberately absent
 * from every sentence here: it is not a billing limit and does not belong in a billing banner.
 *
 * ⚠⚠ DO NOT BLANKET-REMOVE THE "STAY ONLINE" PHRASE. It is load-bearing on the bandwidth
 * surfaces, `docs/PRICING.md` asserts it there, and a sweep would put an error into the regime
 * where it holds. That is the trap app#162 navigated and app#163 was filed rather than fixed in
 * passing because of.
 */
import type { BillingConsequences, CreditPosition } from '../models/useBilling'
import { creditThrottleCause } from './credit.ts'

/** Why an org's billing is limiting it right now — the cause the banner and the alert derive. */
export type BillingLimitCause =
  | 'lapsed'
  | 'cap'
  | 'allowance'
  | 'limit'
  // The two CREDIT causes (app#145). Under credit billing the spend cap does not throttle at all
  // — it bounds credit PURCHASES — and the BALANCE is what the throttle reads
  // (`throttleCreditSQL`). So a paid org throttled on a credit deployment is one of these two,
  // never `cap`.
  | 'credit_empty' // the allowance is used up and the balance is empty
  | 'spending_off' // the allowance is used up and the owner turned off spending the balance
  // The third credit cause (app#269): the allowance is used up, spending is on and the balance is
  // POSITIVE — credit landed while the fail-open stamp had not yet lapsed. The throttle is lifting
  // on its own; nothing here stops a machine either.
  | 'credit_landed'

/**
 * Whether this cause ALSO stops provisioned compute.
 *
 * ⚠ `allowance` is false here and that is not an oversight: on a Free plan the BANDWIDTH
 * allowance blocks transfers, and the COMPUTE allowance is a different meter with its own
 * crossing. A bandwidth banner has no business claiming anything about compute.
 *
 * ⚠ `limit` — the fallback cause, when none of the three branches matched — is false, the
 * fail-safe direction for a claim. Warning about a stop we cannot establish is how a customer
 * learns to discount the warning that matters.
 *
 * ⚠ The spellings are `BillingBanner`'s own, and `throttled_${cause}` is the banner's DISMISSAL
 * KEY, so renaming one here would un-dismiss every banner a customer had already dismissed.
 */
export const stopsProvisionedCompute = (cause: BillingLimitCause): boolean =>
  cause === 'lapsed' || cause === 'credit_empty'

// ⚠⚠ THIS TABLE MIRRORS THE ROUTER'S `billingStopVerdict` (src/router/compute_stops.go) AND MOVES
// WITH IT. Since router#1242 an EMPTY BALANCE stops a running machine (`StopCreditEmpty`), and
// since router#1279 the spend cap bounds automatic refills only, so there is no `spend_cap` stop:
// `cap` is false. `spending_off` is false because the router's gate asks the balance, not the
// owner's switch; `credit_landed` is false because there is money to draw on. Claiming a stop we
// cannot establish is how a customer learns to discount the warning that matters.

/**
 * Whether this cause hides the compute stop rather than ruling it out (app#290).
 *
 * ⚠⚠ `FALSE` FROM `stopsProvisionedCompute` HAS TWO MEANINGS AND ONLY ONE OF THEM IS A FACT. For
 * `allowance` it is knowledge: a Free bandwidth throttle is a different meter and touches no
 * machine. For `cap` it is ignorance. `cap` is not the spend cap on a throttle surface — since
 * router#1279 the cap slows nothing — it is the arm `BillingBanner` and `Usage` fall to when the
 * credit position is NULL, so the reader is a Member (the credit tool is Owner-only), an older
 * router, or an arrears deployment. The real cause is then one of the three credit causes, and
 * `credit_empty` is among them. So the app cannot see whether the machines were stopped, and the
 * sentence it used to render said they were not.
 *
 * ⚠ The claim it replaced was positive and unestablished: *"Your nodes stay online at reduced
 * speed (this is a billing limit, not an outage)."* For a reader whose balance IS empty the
 * router has already ended the ECS task, so the machines are gone and it IS an outage for them.
 * This is the direction the rest of this file fails safe in — "claiming a stop we cannot
 * establish is how a customer learns to discount the warning that matters" — and the same
 * reasoning refuses a claim of NO stop just as firmly.
 *
 * ⚠ It stays gated on the router's `stop_compute`. A false flag is knowledge — the compute stop
 * is not being enforced, so no cause stops a machine — and the plain sentence is then correct for
 * this reader too.
 *
 * ⚠ The cause is not renamed. `throttled_cap` is the banner's DISMISSAL KEY, and renaming it
 * would un-dismiss every banner a customer has already dismissed (app#288).
 */
export const stopIsUnreadable = (cause: BillingLimitCause): boolean => cause === 'cap'

/**
 * The one sentence about what happens to a machine noBGP runs for you.
 *
 * ⚠⚠ IT NAMES WHAT IS LOST AS WELL AS WHAT IS KEPT (app#159). "It keeps its stored files" is
 * `docs/PRICING.md` almost verbatim and is defensible in isolation — "stored files" is a term of
 * art for node storage, which lives on the router under `nodes/<node_id>/` and genuinely does
 * survive. In a one-line caption with no adjacent bullet a customer reads it as *my files on that
 * machine are kept*, and the container filesystem is destroyed: `terraform/shared/mcp.tf` has
 * `mountPoints = []`, no `volume` block and no `ephemeral_storage`. PRICING pairs the sentence
 * with a second bullet; a caption has no second bullet, so the pairing goes inside it.
 *
 * ⚠ The word "suspended" must not come back (router#1078). A customer told a node is suspended
 * infers a second, compute-level suspended state they can un-pause. There is none: the container
 * ends, the node row survives, and re-provisioning by name IS the resume.
 */
/**
 * What a stopped machine keeps, and what it loses — the app#159 pair, in ONE place.
 *
 * ⚠⚠ THE SURFACES THAT SAY IT MUST READ IT FROM HERE AND NEVER RETYPE IT. The Free card's compute
 * line and the spend-cap caption in `Billing.tsx` each used to carry the sentence as their own
 * literal string, and app#159 was filed against the caption — so a guard covering only
 * `PROVISIONED_STOP_CLAUSE` would have let the exact reported defect come back on the exact
 * surface it was reported on, with the suite green.
 *
 * ⚠ THE PLAN CARDS ARE NO LONGER READERS (app#219). Benoit asked for cards that compare rather
 * than explain, so what a stopped machine keeps and loses moved off them to
 * `docs.nobgp.com/plans-and-billing`, which the billing panel now links. The banners and the
 * Usage alert still render it through `throttleConsequence`, and they are what this guards.
 *
 * ⚠ It starts after the verb, because the two sentences do not agree on the subject: the banner
 * says "Each keeps …" of a fleet and a plan card says "It keeps …" of one machine.
 */
export const KEEPS_BUT_NOT_DISK =
  'its name, its permissions and its stored files — but not its own disk — so you start it ' +
  'again under the same name'

const PROVISIONED_STOP_HEAD = 'machines noBGP runs for you are stopped'

export const PROVISIONED_STOP_CLAUSE =
  `${PROVISIONED_STOP_HEAD}. Each keeps ${KEEPS_BUT_NOT_DISK}`

/**
 * The same consequence as ONE sentence, for the arm that states it under an `If`.
 *
 * ⚠⚠ A CONDITION GOVERNS ONE SENTENCE, NOT A PARAGRAPH. `PROVISIONED_STOP_CLAUSE` is two
 * sentences, so `If the credit balance is empty, ${PROVISIONED_STOP_CLAUSE}` hedges the stop and
 * then asserts "Each keeps … but not its own disk — so you start it again under the same name"
 * FLATLY, to a reader whose machines are very likely running. That is a statement of fact about
 * a customer's machines and their disks, and it is the app#196 class of defect — the one this
 * module exists to stop. Joining with `, and` keeps the whole consequence inside the `If`.
 *
 * ⚠ Both forms come from `PROVISIONED_STOP_HEAD` and `KEEPS_BUT_NOT_DISK`, so no sentence is
 * retyped and the two cannot drift apart.
 */
export const PROVISIONED_STOP_CLAUSE_CONDITIONAL =
  `${PROVISIONED_STOP_HEAD}, and each keeps ${KEEPS_BUT_NOT_DISK}`

/**
 * The consequence clause for a bandwidth throttle, which is the only place the two halves appear
 * in one sentence.
 *
 * ⚠⚠ THE STOPPING FORM IS CORRECT FROM THE MOMENT `NOBGP_ENFORCEMENT_ENABLED` IS TRUE, AND THAT
 * FLAG IS FALSE TODAY. Every throttle surface is reachable only when the router has stamped
 * `throttled_until`, which the same flag gates — so a rendered throttle banner means enforcement
 * is on and the stop is real. The LAPSED banner is the exception: it renders off
 * `subscription_status` alone, so between now and the flip it over-warns a lapsed org about a
 * stop that will not come. That is the safe direction and the deliberate one — today's copy
 * under-warns, promising machines stay online in exactly the state that will end them.
 */
/**
 * ⚠⚠ WITH `consequences` PRESENT, THE ROUTER'S FLAGS DECIDE (app#270, router#1306). The paragraph
 * above describes the flags this file used to ASSUME; the payload now carries what they resolve
 * to, and a clause whose boolean is false is dropped. The alert mails already do this, and a
 * customer told a running machine was stopped can restart work that never died.
 *
 * ⚠ `null` — an older router, or a reader who cannot load the summary — keeps every clause,
 * exactly as before. Absence is not evidence that nothing is enforced.
 *
 * ⚠ The cause still gates the stop clause: `stop_compute` says the router WOULD stop the machines
 * of a lapsed org or one with an empty balance (router#1242), and only those two causes reach
 * that stop. A Free allowance throttle is bandwidth-only whatever the flag says.
 *
 * ⚠⚠ THREE OUTCOMES, NOT TWO (app#290). A cause that does not stop compute is either a cause that
 * CANNOT — `allowance` and `credit_landed`, where the app KNOWS no machine is touched — or `cap`,
 * the arm the surfaces fall to when they cannot READ the credit position. The first keeps the
 * plain sentence. The second gets the same consequence behind an `If`, because the reader may be
 * an Owner-less Member of an org whose balance is empty and whose machines the router has
 * already ended.
 *
 * ⚠⚠ `limit` AND `spending_off` ARE IGNORANCE TOO, AND THIS FUNCTION STILL TREATS THEM AS
 * KNOWLEDGE. Do not read their absence from `stopIsUnreadable` as a finding that they are safe.
 * `limit` is the fallback when no branch matched, which is the definition of not knowing (it is
 * what a Member whose `plans` row RLS hides reaches, and that state PERSISTS). `spending_off`
 * is worse than ignorance: `creditThrottleCause` asks the SWITCH first, so an org with spending
 * off and a balance drained to zero is labelled `spending_off`, while the router's gate asks the
 * BALANCE and has already issued `StopCreditEmpty`. Both keep the plain "not an outage" sentence
 * today. app#322 holds them; this change deliberately covers only the arm app#290 reported.
 *
 * ⚠ "At reduced speed" is not gated by `slow_bandwidth`. Every throttle surface renders only
 * while `throttled_until` is stamped, and the evaluator stamps it only while enforcement is on —
 * so the throttle is real whenever this sentence is shown.
 */
export const throttleConsequence = (cause: BillingLimitCause, consequences: BillingConsequences | null = null): string => {
  const enforced = consequences?.stopCompute ?? true
  if (stopsProvisionedCompute(cause) && enforced)
    return `Your own machines stay online at reduced speed, and ${PROVISIONED_STOP_CLAUSE}.`
  if (stopIsUnreadable(cause) && enforced)
    return `Your own machines stay online at reduced speed. If the credit balance is empty, ${PROVISIONED_STOP_CLAUSE_CONDITIONAL}.`
  return 'Your nodes stay online at reduced speed (this is a billing limit, not an outage).'
}

/** The creation refusal, in the app's own words. Rendered only when `refusesCreation` says so. */
export const REFUSE_CLAUSE = 'new nodes, networks and services are refused'

/**
 * Whether this cause ALSO refuses new nodes, networks and services — the one consequence that is
 * live on the deployment we run today (`BillingEnabled` alone).
 *
 * ⚠ `null` returns false, and the callers then keep the wording they had before the field
 * existed: the lapsed banner says "new usage is paused", the throttle banner says nothing of it.
 * The clause is only ever ADDED on the router's word.
 *
 * ⚠ Same causes as the compute stop. `CheckBillingBlock` refuses creation for `lapsed` or
 * `credit_empty` only; the cap bounds automatic refills and refuses nothing DIRECTLY since
 * router#1279. Nothing establishes the refusal for a Free allowance or the other credit causes.
 *
 * ⚠ "Refuses nothing" is about the PREDICATE (nobgp-router#1328). An org with a refill rule armed
 * arrives at `credit_empty` THROUGH its maximum — refills stop at the cap, the balance drains —
 * so a reached maximum is a cause of this refusal even though the cap is named nowhere in it.
 */
export const refusesCreation = (cause: BillingLimitCause, consequences: BillingConsequences | null): boolean =>
  !!consequences?.refuseCreate && (cause === 'lapsed' || cause === 'credit_empty')

/**
 * The throttle reason for a paid org whose CREDIT POSITION THIS READER CANNOT SEE (app#288).
 *
 * ⚠⚠ IT MUST NOT NAME THE SPEND CAP. This is the `cap` cause's third sink of the arrears sentence
 * app#285 removed from the two `cap_*` banners. The arm is reached when `credit` is null: a MEMBER
 * (the credit tool is Owner-only, so the position is never fetched for them), a router that
 * predates the tool, or an arrears deployment. It said "you reached your monthly spend cap" and
 * "Raise the cap to resume full speed". Under credit the cap bounds automatic refills only
 * (router#1195, #1264, #1279) and slows nothing DIRECTLY, so a Member of a throttled paid org was
 * told that the cap slowed the network, and was offered a remedy that they cannot apply and that
 * would not restore the speed.
 *
 * ⚠ The remedy is the half that was wrong for this reader, and it stays wrong (nobgp-router#1328).
 * A reached maximum really can be one hop upstream of this throttle — refills stop, the balance
 * drains — so "raise the cap" is not a false remedy in general. It is false HERE: this arm cannot
 * see the credit position, so it does not know whether a rule is armed, and it is reached by
 * readers who could not raise the cap if it were.
 *
 * ⚠ The allowance is the ONE fact that holds for every cause this reader can be behind. The
 * router throttles a paid org when the included allowance is used up AND the balance cannot pay
 * (`throttleCreditSQL`), so "the allowance is used up" is true for all three credit causes and
 * for the retired arrears trigger as well. The app cannot tell them apart without the position,
 * so it says only the part it knows.
 *
 * ⚠ The remedy names the OWNER, and the sentence is true for each reader: a Member learns who to
 * ask, and an Owner reads their own two actions. Both remedies are Owner actions, so a Member who
 * is told to "raise the cap" is told to do something that their role refuses.
 *
 * ⚠ The CAUSE stays `cap`, and it still keys the banner's dismissal (`throttled_cap`), so a
 * rename would un-dismiss every banner a customer has already dismissed.
 *
 * ⚠⚠ WHAT THIS PARAGRAPH USED TO SAY ABOUT THE CONSEQUENCE CLAUSE WAS WRONG (app#290). It read
 * that the clause was "correct today" because `stopsProvisionedCompute('cap')` is false — but
 * that predicate answers ignorance here, not knowledge, so the sentence it selected PROMISED the
 * machines were running. `stopIsUnreadable` now selects a conditional sentence for this arm.
 * `refusesCreation('cap', …)` is false and stays a fact: `CheckBillingBlock` refuses creation on
 * the verdict, which this reader's org may or may not carry, and a refusal is visible to them the
 * moment they try. A silent stop is not.
 */
export const UNREADABLE_CREDIT_REASON = 'your included allowance is used up'

export const UNREADABLE_CREDIT_FIX = 'An organization Owner can add credit or upgrade for a larger allowance.'

/**
 * Why a throttled org is throttled, from the state every throttle surface reads (app#323).
 *
 * ⚠⚠ ONE DERIVATION, BECAUSE THE CAUSE DECIDES THE CONSEQUENCE. `throttleConsequence` says the
 * machines noBGP runs for the org are stopped for `lapsed` and `credit_empty`, and that the nodes
 * stay online for `allowance`. A surface that derives no cause, or derives it its own way, can say
 * "not an outage" beside a banner that says the machines are stopped. The node page did exactly
 * that: it rendered a literal and never asked the cause.
 *
 * The arms are `BillingBanner`'s, in its order: lapse, then credit, then the paid fallback, then
 * Free. `Usage` and `TrafficAllowance` read this function. The banner keeps its own arms, because
 * each arm there also sets a reason, a fix and an action; it adds one input this function does not
 * take: a `cap_100` alert on a plan this reader cannot see. There the banner gives `cap` and this
 * gives `limit`, so the banner renders the conditional stop sentence and the other two render the
 * plain one. `Usage` had that gap before this function existed; app#322 holds `limit`.
 */
export const throttleLimitCause = (
  status: string,
  isPaid: boolean,
  isFree: boolean,
  credit: CreditPosition | null,
): BillingLimitCause => {
  if (isPaid && (status === 'unpaid' || status === 'canceled')) return 'lapsed'
  if (credit?.creditBilled && isPaid) return creditThrottleCause(credit)
  if (isPaid) return 'cap'
  if (isFree) return 'allowance'
  return 'limit'
}
